<template>
    <b-overlay :show="isSaving">
        <p v-if="action === 'create-shipped-with-issues'">
            Geef per product het aantal op waar de klacht over gaat.<br/>
            Er wordt automatisch een crediteringsvoorstel aangemaakt.
        </p>
        <p v-else-if="action === 'add-extra-order-lines'">
            Geef per product het aantal op om na te leveren.<br />
            Elk nageleverd product wordt vastgelegd met een prijs van {{ 0 | price }}.
        </p>
        <p v-else-if="action === 'add-extra-order-lines-and-refund-remaining'">
            Geef per product het aantal op om na te leveren.<br />
            Elk nageleverd product wordt vastgelegd met een prijs van {{ 0 | price }}.<br/>
            <strong>Voor de rest van de producten wordt een creditering aangemaakt!</strong>
        </p>

        <form @submit.prevent="submit">
        <b-table-simple borderless small>
            <b-thead>
                <tr>
                    <b-th style="width:130px;">Aantal</b-th>
                    <b-th style="width:80px;">Totaal</b-th>
                    <b-th style="width:105px;">Artikel nr</b-th>
                    <b-th>Naam</b-th>
                    <b-th v-if="showStockInfo">
                        Voorraad
                    </b-th>
                    <b-th style="width:100px;">
                        <span v-if="action === 'create-shipped-with-issues'">Totaalprijs</span>
                        <span v-if="action === 'add-extra-order-lines'">Oorspronkelijke prijs</span>
                    </b-th>
                </tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="dispatchRequestLine in applicableDispatchRequestsLines" :key="dispatchRequestLine.id">
                    <b-td>
                        <b-input v-model.number="selectedProductsPerLine[dispatchRequestLine.id]" type="number" min="0" :max="dispatchRequestLine.quantity"
                                 style="width:60px;" size="sm" class="d-inline-block"
                        />
                        <b-button size="sm" variant="link" @click="selectedProductsPerLine[dispatchRequestLine.id.toString()] = dispatchRequestLine.quantity" class="shadow-none">alles</b-button>
                    </b-td>
                    <b-td>{{ dispatchRequestLine.quantity }}</b-td>
                    <b-td>{{ dispatchRequestLine.product_identifier }}</b-td>
                    <b-td>{{ dispatchRequestLine.product_name ?? getSalesOrderLine(dispatchRequestLine.sales_order_line_id)?.product_name }}</b-td>
                    <b-td v-if="showStockInfo">
                        <product-stock-status :product-identifier="dispatchRequestLine.product_identifier" :quantity="dispatchRequestLine.quantity" auto-check/>
                    </b-td>
                    <b-td>
                        {{ dispatchRequestLine.total_refundable_amount | price }}
                    </b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-row>
            <b-col v-if="action === 'add-extra-order-lines' || action === 'add-extra-order-lines-and-refund-remaining'" class="pb-1">
                Beloofde leverdatum:
                <b-input type="date" v-model="promisedDeliveryDate" :min="tomorrow" size="sm" class="d-inline-block" style="width:150px;"/>
            </b-col>
            <b-col class="text-right mr-1 pb-1">
                <b-button v-if="isModal" variant="secondary" size="sm" class="ml-2" @click="$emit('close')">{{ $t('omt.gui.general.action.close') }}
                </b-button>

                <b-button type="submit" variant="primary" size="sm" class="ml-2"
                          :disabled="isSubmitDisabled"
                >
                    <span v-if="action === 'create-shipped-with-issues'">
                        {{ $t('omt.gui.orders.dispatch-requests.final-action-titles.create-shipped-with-issues') }}
                    </span>
                    <span v-else-if="action === 'add-extra-order-lines'">
                        {{ $t('omt.gui.orders.dispatch-requests.final-action-titles.add-extra-order-lines') }}
                    </span>
                    <span v-else-if="action === 'add-extra-order-lines-and-refund-remaining'">
                        {{ $t('omt.gui.orders.dispatch-requests.final-action-titles.add-extra-order-lines-and-refund-remaining', { totalSelectedCount: this.totalSelectedCount, totalUnselectedCount: this.totalUnselectedCount }) }}
                    </span>
                    <span v-else-if="action === 'cancel-multiple-lines'">
                        {{ $t('omt.gui.orders.dispatch-requests.final-action-titles.cancel-multiple-lines') }}
                    </span>
                    <span v-else-if="action === 'unable-to-ship-final'">
                        {{ $t('omt.gui.orders.dispatch-requests.final-action-titles.unable-to-ship-final') }}
                    </span>
                </b-button>
            </b-col>
        </b-row>
        </form>
    </b-overlay>
</template>

<script>
import _ from "lodash"
import {DispatchRequestsBase} from "./dispatchRequestsBase";
import store from "../../../store";
import moment from "moment/moment";
import {checkCascadingDispatchRequestHasNoOtherStatuses, checkCascadingDispatchRequestsHaveNoOtherStatuses} from "../../../helpers/dispatch-requests";
import ProductStockStatus from "@/components/ProductStockStatus.vue";

export const ACTION_CREATE_SHIPPED_WITH_ISSUES = 'create-shipped-with-issues'
export const ACTION_ADD_EXTRA_ORDER_LINES = 'add-extra-order-lines'
export const ACTION_ADD_EXTRA_ORDER_LINES_AND_REFUND_REMAINING = 'add-extra-order-lines-and-refund-remaining'
export const ACTION_CANCEL_MULTIPLE_LINES = 'cancel-multiple-lines'
export const ACTION_UNABLE_TO_SHIP_FINAL = 'unable-to-ship-final'

export const DISPATCH_REQUEST_STATUSES_FOR_ACTION_SETTING = {
    'create-shipped-with-issues': 'dispatchRequests.allowShippedWithIssuesFromStatuses',
    'add-extra-order-lines': 'dispatchRequests.allowExtraOrderLinesFromStatuses',
}

export default {
    name: "FinalDispatchRequestActions",
    components: {ProductStockStatus},
    extends: DispatchRequestsBase,

    props: {
        salesOrder: {required: true},
        overrideDispatchRequests: {required: false, default: []},
        action: {default: ACTION_CREATE_SHIPPED_WITH_ISSUES},
        isModal: {required: false, default: true},
    },

    data() {
        return {
            isSaving: false,
            selectedProductsPerLine: {},
            preferredRefundMethod: null,
            promisedDeliveryDate: '',
            errorLineIds: [],
        }
    },

    computed: {
        applicableDispatchRequests() {
            if (this.overrideDispatchRequests.length) {
                return this.overrideDispatchRequests
            }

            return this.getApplicableDispatchRequests(
                this.salesOrder.current_dispatch_requests,
                this.getAllowedStatusesForAction(this.action)
            )
        },
        applicableDispatchRequestsLines() {
            return _.keyBy(this.applicableDispatchRequests.map(drq => drq.dispatch_request_lines).flat(1), 'id')
        },
        dispatchRequestsById() {
            return _.keyBy(this.applicableDispatchRequests, 'id')
        },
        selectedProductsPerLineFiltered() {
            return _.pickBy(this.selectedProductsPerLine, quantity => quantity)
        },
        totalQuantity() {
            return _.sumBy(Object.values(this.applicableDispatchRequestsLines), line => line.quantity)
        },
        totalSelectedCount() {
            return _.sum(Object.values(this.selectedProductsPerLine))
        },
        totalUnselectedCount() {
            return this.totalQuantity - this.totalSelectedCount
        },
        isSubmitDisabled() {
            return !Object.values(this.selectedProductsPerLineFiltered).length && this.action !== 'add-extra-order-lines-and-refund-remaining'
        },
        showStockInfo() {
            return ['add-extra-order-lines-and-refund-remaining', 'add-extra-order-lines'].includes(this.action)
                && this.hasPermission('stockmanagement.view')
        },
        isSubmitDisabled() {
            return !Object.values(this.selectedProductsPerLineFiltered).length && this.action !== 'add-extra-order-lines-and-refund-remaining'
        },
    },

    mounted() {
        // Create a reactive variable for the quantity for each line
        _.each(this.applicableDispatchRequestsLines, line => this.$set(this.selectedProductsPerLine, line.id, 0))

        this.promisedDeliveryDate = this.tomorrow
    },

    methods: {
        async createShippedWithIssues() {
            this.isSaving = true
            await this.getPreferredRefundMethod()

            const firstLineId = Object.keys(this.selectedProductsPerLineFiltered)[0]
            const firstLineDispatchRequest = this.dispatchRequestsById[this.applicableDispatchRequestsLines[firstLineId].dispatch_request_id]

            return this.$http.post(`/api/rest/sales-orders/${this.salesOrder.identifier}/dispatch-requests`, {
                identifier: `${firstLineDispatchRequest.identifier}_complaint_${Date.now()}`,
                dispatch_type: 'other',
                dispatcher_identifier: firstLineDispatchRequest.dispatcher_identifier,
                dispatcher_type: firstLineDispatchRequest.dispatcher_type,
                responsible_location_id: firstLineDispatchRequest.responsible_location_id,
                batch_identifier: null,
                status: 'shipped-with-issues',
                responsible_location_id: firstLineDispatchRequest.responsible_location_id,

                lines: _.map(this.selectedProductsPerLineFiltered, (quantity, lineId) => {
                    const line = this.applicableDispatchRequestsLines[lineId]

                    return {
                        product_identifier: line.product_identifier,
                        quantity: quantity,
                        metadata: {
                            shipped_dispatch_request_line_id: lineId,
                        },
                    }
                }),
            })
                .then(response => {
                    this.$alert('Klacht succesvol aangemaakt')
                    this.$emit('dataChanged')
                    this.$emit('close')
                })
                .catch(e => {
                    console.error(e)
                    this.$alert('Fout bij het aanmaken van de klacht', 'danger')
                    this.isSaving = false
                })
        },
        async replaceLines(status) {
            this.isSaving = true
            await this.getPreferredRefundMethod()

            const firstLineId = Object.keys(this.selectedProductsPerLineFiltered)[0]
            const firstLineDispatchRequest = this.dispatchRequestsById[this.applicableDispatchRequestsLines[firstLineId].dispatch_request_id]
            const linesGroupedByProduct = _.chain(this.selectedProductsPerLineFiltered)
                // Map to an object because groupBy() doesn't get the key as the 2nd argument
                .map((quantity, lineId) => {
                    return {
                        quantity: quantity,
                        lineId: lineId,
                    }
                })
                .groupBy(lineData => {
                    const line = this.applicableDispatchRequestsLines[lineData.lineId]
                    return line.product_identifier
                })
                .value()

            return this.$http.post(`/api/rest/dispatch-requests`, [{
                dispatch_type_identifier: firstLineDispatchRequest.dispatch_type.identifier,
                dispatcher_identifier: firstLineDispatchRequest.dispatcher_identifier,
                dispatcher_type: firstLineDispatchRequest.dispatcher_type,
                dispatch_date: moment().format('YYYY-MM-DD'),
                carrier_identifier: firstLineDispatchRequest.carrier?.identifier,
                carrier_method_identifier: firstLineDispatchRequest.carrier_method?.identifier,
                status: status,

                lines: _.map(linesGroupedByProduct, (lines) => {
                    return {
                        old_line_ids: lines.map(lineData => lineData.lineId),
                        quantity: _.sumBy(lines, lineData => lineData.quantity),
                    }
                }),
            }])
                .then(response => {
                    this.$alert('De producten zijn geannuleerd')
                    this.$emit('dataChanged')
                    this.$emit('close')
                })
                .catch(e => {
                    console.error(e)
                    this.$alert('Fout bij het annuleren', 'danger')
                    this.isSaving = false
                })
        },
        async getPreferredRefundMethod() {
            return this.$http.get(`/api/rest/sales-orders/${this.salesOrder.identifier}`, {
                params: {
                    include: 'preferred_refund_method',
                }
            })
                .then(response => {
                    this.preferredRefundMethod = response.data.preferred_refund_method
                })
        },
        async createExtraOrderLines() {
            this.isSaving = true
            this.errorLineIds = []
            await this.addExtraOrderLines(this.selectedProductsPerLineFiltered)

            if (!this.errorLineIds.length) {
                this.$alert(`De extra orderregels zijn toegevoegd`)
                this.$emit('dataChanged')
                this.$emit('close')
            }
            else {
                this.$alert('Niet alle orderregels zijn succesvol toegevoegd. Controleer goed welke wel zijn gelukt en probeer het daarna pas opnieuw voor de mislukte regels.', 'danger', false)
                this.$emit('close')
            }
        },
        async createExtraOrderLinesAndRefundRemaining() {
            this.isSaving = true
            this.errorLineIds = []

            this.$http.post(`/api/rest/dispatch-requests/id:${this.applicableDispatchRequests[0].id}/resend-and-refund`, {
                resend_lines: _.map(this.selectedProductsPerLine, (quantity, lineId) => ({
                    line_id: lineId,
                    quantity: quantity,
                })),
                promised_delivery_date: this.promisedDeliveryDate,
            })
                .then(response => {
                    this.$alert('De producten zijn verwerkt voor nalevering en/of creditering')
                    this.$emit('dataChanged')
                    this.$router.push({
                        name: 'OrderDetailOverview',
                        params: {salesOrderIdentifier: this.salesOrder.identifier}
                    })
                })
                .catch(error => {
                    this.$alert('Fout bij het verwerken. Probeer het nogmaals of neem contact op met de beheerder', 'danger')
                })
                .finally(() => this.isSaving = false)
        },
        async addExtraOrderLines(selectedProductsPerLineFiltered) {
            const lineId = Object.keys(selectedProductsPerLineFiltered)[0]
            const quantity = selectedProductsPerLineFiltered[lineId]
            const line = this.applicableDispatchRequestsLines[lineId]
            const salesOrderLine = this.getSalesOrderLine(line.sales_order_line_id)

            await this.$http.post(`/api/rest/sales-orders/${this.salesOrder.identifier}/sales-order-lines`, {
                product_identifier: line.product_identifier,
                product_name: line.product_name ?? salesOrderLine.product_name,
                quantity: quantity,
                promised_delivery_date: this.promisedDeliveryDate,
                metadata: {
                    originating_dispatch_request_line_id: lineId,
                },
            })
                .then(response => {

                })
                .catch(e => {
                    console.error(e)
                    this.errorLineIds.push(lineId)
                })
                .then(async () => {
                    delete selectedProductsPerLineFiltered[lineId]
                    if (Object.keys(selectedProductsPerLineFiltered).length > 0) {
                        await this.addExtraOrderLines(selectedProductsPerLineFiltered)
                    }
                })
        },
        getApplicableDispatchRequests(dispatchRequests, allowedStatuses) {
            // Geef alleen DRQs terug die in aanmerking komen voor een final action.
            // Dat zijn DRQs die een status hebben die ook in `allowedStatuses` staat.
            // En, als dat is ingesteld dmv de setting `dispatchRequests.finalDispatchRequestActions.allowWithActiveCrossdock`, mogen DRQs geen crossdock-link hebben die op `to-be-requested` staat.
            return dispatchRequests.filter(drq =>
                allowedStatuses.indexOf(drq.status) !== -1
                && drq.dispatch_action !== 'crossdocking'
                && (
                    store.state.config.settings['dispatchRequests.finalDispatchRequestActions.allowWithActiveCrossdock']
                    || checkCascadingDispatchRequestsHaveNoOtherStatuses(drq, dispatchRequests, ['to-be-requested'])
                )
            )
        },
        getAllowedStatusesForAction(action) {
            switch (action) {
                case ACTION_CREATE_SHIPPED_WITH_ISSUES:
                    return store.state.config.settings[DISPATCH_REQUEST_STATUSES_FOR_ACTION_SETTING[ACTION_CREATE_SHIPPED_WITH_ISSUES]] ?? []
                case ACTION_ADD_EXTRA_ORDER_LINES:
                    return store.state.config.settings[DISPATCH_REQUEST_STATUSES_FOR_ACTION_SETTING[ACTION_ADD_EXTRA_ORDER_LINES]] ?? []
                case ACTION_ADD_EXTRA_ORDER_LINES_AND_REFUND_REMAINING:
                    return store.state.config.settings[DISPATCH_REQUEST_STATUSES_FOR_ACTION_SETTING[ACTION_ADD_EXTRA_ORDER_LINES]] ?? []
                case ACTION_CANCEL_MULTIPLE_LINES:
                    return store.getters.getFromStatusesForRerouteToStatus('cancelled') ?? []
                case ACTION_UNABLE_TO_SHIP_FINAL:
                    return store.getters.getFromStatusesForRerouteToStatus('unable-to-ship-final') ?? []
            }
        },
        submit() {
            switch (this.action) {
                case 'create-shipped-with-issues':
                    this.createShippedWithIssues();
                    break;
                case 'add-extra-order-lines':
                    this.createExtraOrderLines();
                    break;
                case 'add-extra-order-lines-and-refund-remaining':
                    this.createExtraOrderLinesAndRefundRemaining();
                    break;
                case 'cancel-multiple-lines':
                    this.replaceLines('cancelled');
                    break;
                case 'unable-to-ship-final':
                    this.replaceLines('unable-to-ship-final');
                    break;
            }
        },
    },
}
</script>

<style scoped>
td {
    line-height: 240%;
}
</style>
